import {
  ArrowUpRight,
  Check,
  Heart,
  Music2,
  Sparkles,
  Star,
  Zap,
  ArrowRight,
  Mail,
} from "lucide-react";
import { BrandEnquiry } from "../components/BrandEnquiry";
import { Toaster } from "../components/ui/sonner";
import { FeeFAQ } from "../components/FeeFAQ";

const JOTFORM = "https://www.jotform.com/app/250226203872147";

const HERO_IMG = "/hero-collab.jpg";
const MID_IMG =
  "https://images.unsplash.com/photo-1665327465092-a5a05e70bd20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx0aWt0b2slMjBjb250ZW50JTIwY3JlYXRvciUyMHN0cmVhbWluZyUyMHJpbmclMjBsaWdodHxlbnwwfHx8fDE3NzcwNTc4MzJ8MA&ixlib=rb-4.1.0&q=85";
const CLOSE_IMG =
  "https://images.pexels.com/photos/17580425/pexels-photo-17580425.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

/* ---------- reusable pieces ---------- */
const Pill = ({ children, tone = "lime", className = "" }) => {
  const tones = {
    lime: "bg-lime text-ink",
    coral: "bg-coral text-ink",
    pink: "bg-pink text-white",
    sky: "bg-sky text-ink",
    ink: "bg-ink text-white",
    white: "bg-white text-ink border border-ink/15",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] font-semibold tracking-wide ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
};

const CTA = ({ href, children, tone = "ink", testid, big = false }) => {
  const external = !href.startsWith("#");
  const tones = {
    ink: "bg-ink text-white hover:bg-pink",
    lime: "bg-lime text-ink hover:bg-ink hover:text-lime",
    coral: "bg-coral text-ink hover:bg-ink hover:text-coral",
    pink: "bg-pink text-white hover:bg-ink",
    white: "bg-white text-ink border-2 border-ink hover:bg-ink hover:text-white",
  };
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      data-testid={testid}
      className={`group inline-flex items-center gap-2 rounded-full font-semibold transition-colors ${
        big ? "px-8 py-5 text-[15px]" : "px-6 py-3.5 text-[14px]"
      } ${tones[tone]}`}
    >
      {children}
      <ArrowUpRight
        size={big ? 20 : 16}
        className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
      />
    </a>
  );
};

const SectionLabel = ({ n, children, color = "pink" }) => {
  const colors = {
    pink: "text-pink",
    lime: "text-lime",
    coral: "text-coral",
    sky: "text-sky",
  };
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className={`text-[12px] font-bold tracking-[0.2em] uppercase ${colors[color]}`}>
        ({n})
      </span>
      <span className="text-[12px] font-semibold tracking-[0.15em] uppercase text-ink/60">
        {children}
      </span>
    </div>
  );
};

/* ---------- sections ---------- */

const Nav = () => (
  <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5 bg-white border-b border-ink/10">
    <div
      className="font-display text-[22px] sm:text-[26px] text-ink flex items-center gap-2"
      data-testid="wordmark"
    >
      Wanderpreneur<span className="text-pink">.</span>
    </div>
    <nav className="hidden md:flex items-center gap-8 text-[14px] font-medium text-ink/70">
      <a href="#network" className="hover:text-pink transition-colors">Network</a>
      <a href="#benefits" className="hover:text-pink transition-colors">Benefits</a>
      <a href="#fees" className="hover:text-pink transition-colors">Fees</a>
      <a href="#voices" className="hover:text-pink transition-colors">Voices</a>
    </nav>
    <div className="flex items-center gap-2">
      <a
        href="mailto:WPAAgency2026@gmail.com"
        data-testid="nav-email"
        className="inline-flex items-center gap-2 border-2 border-ink text-ink px-4 py-2 rounded-full text-[13px] font-semibold hover:bg-pink hover:border-pink hover:text-white transition-colors"
      >
        <Mail size={14} /> Email us
      </a>
      <a
        href="https://form.jotform.com/243437768454064"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="nav-apply"
        className="hidden sm:inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 rounded-full text-[13px] font-semibold hover:bg-pink transition-colors"
      >
        Application <ArrowUpRight size={14} />
      </a>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative bg-white overflow-hidden">
    {/* playful blobs */}
    <div className="pointer-events-none absolute -top-10 right-10 w-40 h-40 bg-lime rounded-full float1 opacity-90" />
    <div className="pointer-events-none absolute top-[40%] -left-20 w-60 h-60 bg-pink/20 rounded-full float2 blur-2xl" />
    <div className="pointer-events-none absolute bottom-10 right-[25%] w-32 h-32 bg-coral rounded-full float1 opacity-60" />

    <div className="relative z-10 px-6 sm:px-10 pt-14 pb-20 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-10">
        <Pill tone="lime">
          <Sparkles size={14} /> Accepting Creators
        </Pill>
        <Pill tone="pink">
          <Star size={14} /> Accepting Brands
        </Pill>
        <Pill tone="white">
          <Heart size={14} /> TikTok · Instagram · Brand Partnerships
        </Pill>
      </div>

      <h1
        className="font-display text-ink leading-[0.95]"
        style={{ fontSize: "clamp(40px, 6.5vw, 108px)" }}
        data-testid="hero-headline"
      >
        Connecting <span className="text-sky">Brands</span> with{" "}
        <span className="relative inline-block">
          <span className="relative z-10">Creators.</span>
          <span className="absolute inset-x-0 bottom-2 h-[0.35em] bg-lime -z-0 rotate--3 rounded-sm" />
        </span>{" "}
        Empowering Creators with{" "}
        <span className="text-pink italic">Opportunity.</span>{" "}
        <span className="inline-block float1">
          <Zap className="inline text-coral" size={62} fill="currentColor" />
        </span>
      </h1>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <p
          className="md:col-span-7 text-ink/80 text-[17px] sm:text-[20px] leading-relaxed max-w-[60ch]"
          data-testid="hero-sub"
        >
          WPA Agency is evolving to become more than a creator support agency.
          We are building a platform that empowers content creators to grow
          their digital presence, develop their influence, and turn their
          creativity into meaningful opportunities.
        </p>
      </div>

      {/* hero image with rounded frame + sticker */}
      <div className="mt-16 relative">
        <div className="relative overflow-hidden rounded-[32px] border-4 border-ink shadow-pop-lime">
          <img
            src={HERO_IMG}
            alt="Creator and brand partner filming a collab launch"
            className="w-full h-[420px] sm:h-[560px] object-cover object-[60%_center] sm:object-top"
            data-testid="hero-image"
          />
          <div className="absolute bottom-6 left-6 right-6 flex flex-wrap items-end justify-between gap-3">
            <Pill tone="white">LIVE · GROW · MONETIZE</Pill>
            <Pill tone="ink">
              <Heart size={12} fill="currentColor" className="text-pink" /> Creator-first
            </Pill>
          </div>
        </div>
        {/* sticker */}
        <div className="hidden sm:flex absolute -top-6 -right-4 w-28 h-28 bg-pink rounded-full items-center justify-center rotate-6 border-4 border-ink shadow-pop">
          <div className="text-center leading-tight text-white font-display text-[13px]">
            FEE ON<br/>
            <span className="text-[10px] font-semibold tracking-wider">DEALS ONLY</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => {
  const items = [
    ["2", "Sides served — creators & brands", "lime"],
    ["1:1", "Personal coaching", "coral"],
    ["3", "Countries (US · CAN · UK)", "sky"],
    ["24/7", "Creator support", "pink"],
  ];
  return (
    <section className="relative z-10 bg-ink">
      <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4">
        {items.map(([v, l, c], i) => (
          <div
            key={l}
            className={`px-6 sm:px-10 py-10 flex flex-col gap-3 ${
              i !== 0 ? "md:border-l border-white/10" : ""
            } ${i === 1 || i === 3 ? "border-l border-white/10" : ""} ${
              i >= 2 ? "border-t md:border-t-0 border-white/10" : ""
            }`}
            data-testid={`stat-${i}`}
          >
            <span
              className={`font-display text-[56px] sm:text-[72px] leading-none ${
                c === "lime" ? "text-lime" :
                c === "coral" ? "text-coral" :
                c === "sky" ? "text-sky" : "text-pink"
              }`}
            >
              {v}
            </span>
            <span className="text-[12px] sm:text-[13px] font-semibold tracking-wide uppercase text-cream/70">
              {l}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const Network = () => (
  <section id="network" className="relative bg-white">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <SectionLabel n="01" color="pink">The Network</SectionLabel>
      <h2
        className="font-display text-ink leading-[1] max-w-[20ch]"
        style={{ fontSize: "clamp(40px, 6vw, 92px)" }}
      >
        WPA — Now a <span className="text-pink">Bridge</span>
      </h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <p className="md:col-span-6 text-ink/80 text-[17px] leading-relaxed" data-testid="network-bridge">
          WPA serves as the{" "}
          <span className="bg-sky px-1 rounded font-semibold">bridge between creators and brands</span>
          {" "}— helping businesses connect with the right talent while
          positioning creators for brand sponsorships, collaborations, and paid
          promotional opportunities.
        </p>
        <p className="md:col-span-5 md:col-start-8 text-ink/70 text-[17px] leading-relaxed" data-testid="network-mission">
          Our mission is to develop confident, business-minded creators who
          understand their value, maximize their influence, and build
          opportunities beyond social media views and followers.
        </p>
        <p className="md:col-span-6 text-ink/80 text-[17px] leading-relaxed">
          The Wanderpreneur Creators Network is a{" "}
          <span className="bg-lime px-1 rounded font-semibold">
            community-focused agency
          </span>{" "}
          dedicated to empowering content creators to grow their brand, reach
          new audiences, and monetize their passion.
        </p>
        <p className="md:col-span-5 md:col-start-8 text-ink/70 text-[17px] leading-relaxed">
          As part of the TikTok Creators Network — and now supporting creators
          on Instagram too — we offer exclusive resources, brand
          collaborations, professional opportunities and educational support to
          help content creators build real influence in the rapidly evolving
          digital landscape.
        </p>
      </div>
    </div>
  </section>
);

const BENEFITS = [
  ["01", "Tailored Growth Plans", "Customized strategies that cater to your individual goals, niche, and audience — not a generic playbook.", "lime"],
  ["02", "Enhanced Visibility", "Spotlight features, official Agency Host events, and promotional campaigns built to amplify you.", "pink"],
  ["03", "No Upfront Fees", "Creators pay nothing upfront. WPA earns a percentage only once a deal is reached — so we're fully invested in landing you the right partnerships.", "coral"],
  ["04", "Skill Development", "Training sessions on livestreaming, content craft, and audience engagement from people who've done it.", "sky"],
  ["05", "Stress-Free Support", "Dedicated help with violations, appeals, unfair bans, or policy misunderstandings — we've got your back.", "lime"],
  ["06", "Collaborative Community", "Build real relationships with other creators and brands in the network for mutual, compounding growth.", "pink"],
];

const Benefits = () => (
  <section id="benefits" className="relative bg-cream">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <SectionLabel n="02" color="pink">Why creators join</SectionLabel>
      <h2
        className="font-display text-ink leading-[1] max-w-[20ch]"
        style={{ fontSize: "clamp(40px, 6vw, 92px)" }}
      >
        The benefits of <span className="text-pink">membership.</span>
      </h2>
      <p className="mt-6 text-ink/70 text-[17px] leading-relaxed max-w-[62ch]">
        Six concrete advantages designed to take serious creators from "going
        live" to running a real, sustainable business across TikTok, Instagram
        and brand partnerships.
      </p>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {BENEFITS.map(([n, t, s, c]) => {
          const tint = {
            lime: "bg-lime/20",
            pink: "bg-pink/20",
            coral: "bg-coral/20",
            sky: "bg-sky/20",
          }[c];
          const chip = {
            lime: "bg-lime text-ink",
            pink: "bg-pink text-white",
            coral: "bg-coral text-ink",
            sky: "bg-sky text-ink",
          }[c];
          return (
            <div
              key={n}
              className={`group relative rounded-[28px] border-2 border-ink p-8 bg-white hover:${tint} transition-colors flex flex-col gap-5 min-h-[260px]`}
              data-testid={`benefit-${n}`}
            >
              <span
                className={`w-11 h-11 rounded-full ${chip} flex items-center justify-center font-display text-[16px]`}
              >
                {n}
              </span>
              <h3 className="font-display text-ink text-[26px] leading-[1.1]">
                {t}
              </h3>
              <p className="text-ink/70 text-[15px] leading-relaxed">{s}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const words = [
    ["CREATORS", "pink"],
    ["STREAMERS", "lime"],
    ["STORYTELLERS", "coral"],
    ["VISIONARIES", "sky"],
    ["BUILDERS", "pink"],
    ["HOSTS", "lime"],
    ["PERFORMERS", "coral"],
    ["ENTREPRENEURS", "sky"],
    ["BRANDS", "pink"],
    ["INFLUENCERS", "lime"],
  ];
  return (
    <section className="relative bg-ink overflow-hidden border-y-4 border-ink py-6">
      <div className="flex gap-10 whitespace-nowrap marquee">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex gap-10 shrink-0 items-center">
            {words.map(([w, c], j) => (
              <span
                key={`${i}-${j}`}
                className={`font-display text-[44px] sm:text-[64px] leading-none ${
                  c === "pink" ? "text-pink" :
                  c === "lime" ? "text-lime" :
                  c === "coral" ? "text-coral" : "text-sky"
                }`}
              >
                {w} <span className="text-white">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

const WhoShouldJoin = () => (
  <section className="relative bg-white">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-5 relative">
        <div className="relative overflow-hidden rounded-[32px] border-4 border-ink shadow-pop-pink">
          <img
            src={MID_IMG}
            alt="Creator setting up a ring light for a livestream"
            className="w-full h-[480px] object-cover"
            data-testid="who-image"
          />
          <div className="absolute top-4 left-4">
            <Pill tone="white">The creator we build for</Pill>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <Pill tone="ink">Serious · Consistent · Ready to evolve</Pill>
          </div>
        </div>
        {/* sticker */}
        <div className="hidden sm:flex absolute -bottom-6 -left-4 bg-lime border-4 border-ink rounded-full px-5 py-3 rotate--6 shadow-pop">
          <span className="font-display text-ink text-[15px] tracking-tight">you pass? you apply ✦</span>
        </div>
      </div>

      <div className="md:col-span-7 md:pl-6">
        <SectionLabel n="03" color="coral">Who should join</SectionLabel>
        <h2
          className="font-display text-ink leading-[1]"
          style={{ fontSize: "clamp(40px, 6vw, 88px)" }}
        >
          If this sounds like <span className="text-coral">you — apply.</span>
        </h2>
        <p className="mt-6 text-ink/70 text-[17px] leading-relaxed max-w-[58ch]">
          WPA Agency is built for creators who are ready to treat their craft
          like a career.
        </p>

        <ul className="mt-8 space-y-4" data-testid="who-bullets">
          {[
            "Serious and passionate about going live with high-quality, engaging content",
            "Looking to monetize your passion and expand your opportunities",
            "Want access to professional support, advice, and tools to grow your brand",
            "Enjoy collaborating with other creators and building a real community",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3">
              <span className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-lime border-2 border-ink flex items-center justify-center">
                <Check size={14} strokeWidth={3} className="text-ink" />
              </span>
              <span className="text-ink/80 text-[16px] leading-relaxed">{t}</span>
            </li>
          ))}
        </ul>

        <div className="mt-10">
          <CTA href="https://form.jotform.com/243437768454064" tone="pink" testid="who-cta">
            Apply
          </CTA>
        </div>
      </div>
    </div>
  </section>
);

const PILLARS = [
  ["01", "Creative Mentorship", "Work closely with experienced industry professionals for guidance on content strategy, TikTok profile growth, and video quality.", "lime"],
  ["02", "Industry Partnerships", "Partnerships with innovative 3rd party industry leaders who have a proven track record of success building million-dollar brands.", "pink"],
  ["03", "Analytics & Insights", "Gain access to advanced analytics to track video performance, identify growth trends, and optimize what's actually working.", "coral"],
  ["04", "Content Strategy", "Receive tailored advice on content creation, rising trends, and engagement tactics so your videos perform at their best.", "sky"],
];

const Pillars = () => (
  <section className="relative bg-white">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <SectionLabel n="04" color="pink">What sets us apart</SectionLabel>
      <h2
        className="font-display text-ink leading-[1] max-w-[18ch]"
        style={{ fontSize: "clamp(40px, 6vw, 92px)" }}
      >
        Development and support that <span className="text-pink">compounds.</span>
      </h2>
      <p className="mt-6 text-ink/70 text-[17px] leading-relaxed max-w-[60ch]">
        Accepting creators from the U.S., Canada, and the UK. Four pillars
        hold the entire network together.
      </p>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PILLARS.map(([n, t, d, c]) => {
          const chip = { lime: "bg-lime", pink: "bg-pink text-white", sky: "bg-sky", coral: "bg-coral" }[c];
          return (
            <div
              key={n}
              className="rounded-[28px] border-2 border-ink p-8 bg-white min-h-[280px] flex flex-col justify-between"
              data-testid={`pillar-${n}`}
            >
              <span className={`w-12 h-12 rounded-full ${chip} border-2 border-ink flex items-center justify-center font-display text-[16px]`}>
                {n}
              </span>
              <div>
                <h3 className="font-display text-ink text-[26px] leading-[1.1] mb-2">{t}</h3>
                <p className="text-ink/70 text-[15px] leading-relaxed">{d}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const SERVICES = [
  ["01", "Comprehensive Support", "End-to-end management of live streams — technical support, creative direction, and audience engagement strategies. Includes coaching sessions and feedback to improve live content quality and maximize earnings."],
  ["02", "Brand Partnerships", "We connect creators with brands that fit their voice — sponsored content, campaigns, and paid collabs, plus live gifting strategy and revenue programs, all without giving up your creative control or a cut of your earnings."],
  ["03", "Growth Resources", "Tools, playbooks, and templates tailored to your niche, built on what's actually working on TikTok's For You page and Instagram Reels right now."],
  ["04", "Community Building", "Dedicated spaces, meetups, and collabs that turn fellow creators into collaborators — and audiences into loyal fans."],
];

const SIDES = [
  ["For Creators", "Grow your presence. Build a business.", [
    "Through personalized coaching, training, and strategic development, we help creators strengthen their presence on TikTok and Instagram, master LIVE streaming, engage their audiences, and build sustainable creator businesses.",
    "Beyond content creation, WPA prepares creators to enter the world of brand partnerships with confidence. We teach creators how to position themselves professionally, develop compelling media kits, pitch to brands, negotiate collaborations, and create content that delivers value to both their audiences and brand partners.",
  ], "bg-lime"],
  ["For Brands", "Find creators who carry your message.", [
    "For brands, WPA provides access to a network of appropriately trained and prepared creators who understand how to communicate brand messaging, engage audiences, and create authentic promotional content that builds brand awareness.",
    "We help brands identify creators whose skills, audiences, and personal brands align with their marketing objectives.",
  ], "bg-pink text-white"],
];

const MODEL = [
  ["Creators", "Brands", "Training, development, professional positioning, and access to brand collaborations.", "bg-lime", "bg-pink text-white"],
  ["Brands", "Creators", "Creator sourcing, talent matching, campaign support, and brand awareness opportunities.", "bg-pink text-white", "bg-lime"],
];

const TwoSidedModel = () => (
  <div className="mt-12 bg-ink rounded-[28px] border-4 border-ink p-5 sm:p-10" data-testid="two-sided-model">
    <p className="text-[12px] font-bold tracking-[0.2em] uppercase text-lime mb-6">
      The WPA 2-sided business model
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {MODEL.map(([from, to, desc, fromC, toC], i) => (
        <div key={from} className="flex flex-col gap-4" data-testid={`model-flow-${i}`}>
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 font-display text-[14px] sm:text-[26px]">
            <span className={`${fromC} border-2 border-white rounded-full px-2.5 sm:px-4 py-1`}>{from}</span>
            <ArrowRight className="text-white" size={16} />
            <span className="bg-sky text-ink border-2 border-white rounded-full px-2.5 sm:px-4 py-1">WPA</span>
            <ArrowRight className="text-white" size={16} />
            <span className={`${toC} border-2 border-white rounded-full px-2.5 sm:px-4 py-1`}>{to}</span>
          </div>
          <p className="text-cream/80 text-[16px] leading-relaxed">{desc}</p>
        </div>
      ))}
    </div>
    <p className="mt-8 pt-6 border-t border-white/15 text-cream/80 text-[14px] sm:text-[15px] leading-relaxed" data-testid="model-fee-note">
      <span className="text-lime font-semibold">How WPA is paid:</span> no upfront fees for creators — WPA earns a percentage once a deal is reached. Brand fees are set after the campaign scope is shared.
    </p>
  </div>
);

const Services = () => (
  <section className="relative bg-sky">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-[12px] font-bold tracking-[0.2em] uppercase text-pink">(05)</span>
        <span className="text-[12px] font-semibold tracking-[0.15em] uppercase text-ink/70">Services</span>
      </div>
      <h2
        className="font-display text-ink leading-[1] max-w-[18ch]"
        style={{ fontSize: "clamp(40px, 6vw, 92px)" }}
      >
        Everything you need, <span className="text-white">in one place.</span>
      </h2>
      <p className="mt-6 text-ink/80 text-[17px] leading-relaxed max-w-[60ch]">
        WPA serves as the connection point between both sides — preparing
        creators for professional opportunities while helping brands discover
        talent that can effectively communicate their message and connect with
        their target audiences.
      </p>

      <TwoSidedModel />

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {SIDES.map(([label, title, paras, chip], i) => (
          <div
            key={label}
            className="bg-white rounded-[28px] border-4 border-ink p-8 sm:p-10 flex flex-col gap-4 shadow-pop"
            data-testid={`side-${i}`}
          >
            <span className={`self-start ${chip} border-2 border-ink rounded-full px-4 py-1 text-[12px] font-bold tracking-wider uppercase`}>
              {label}
            </span>
            <h3 className="font-display text-ink text-[30px] sm:text-[38px] leading-[1.05]">{title}</h3>
            {paras.map((p) => (
              <p key={p.slice(0, 20)} className="text-ink/75 text-[16px] leading-relaxed">{p}</p>
            ))}
            {i === 1 && (
              <div className="mt-auto pt-2">
                <CTA href={BRAND_MAIL} tone="pink" testid="brands-card-cta">Find Creators for your Brand</CTA>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {SERVICES.map(([n, t, d]) => (
          <div
            key={n}
            className="bg-white rounded-[28px] border-2 border-ink p-8 flex flex-col gap-4 min-h-[240px]"
            data-testid={`service-${n}`}
          >
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-pink text-white border-2 border-ink flex items-center justify-center font-display text-[16px]">
                {n}
              </span>
              <h3 className="font-display text-ink text-[26px] sm:text-[30px] leading-[1.05]">
                {t}
              </h3>
            </div>
            <p className="text-ink/75 text-[15px] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BRAND_MAIL = "#brand-enquiry";

const KIT_STATS = [
  ["30K", "TikTok followers", "bg-lime"],
  ["40K", "Monthly views", "bg-sky"],
  ["87%", "Engagement rate", "bg-pink text-white"],
];

const MediaKit = () => (
  <section id="media-kit" className="relative bg-cream">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <SectionLabel n="06" color="pink">Example media kit</SectionLabel>
      <h2 className="font-display text-ink leading-[1] max-w-[20ch]" style={{ fontSize: "clamp(40px, 6vw, 92px)" }}>
        How WPA creators <span className="text-pink">show up to brands.</span>
      </h2>
      <p className="mt-6 text-ink/70 text-[17px] leading-relaxed max-w-[60ch]">
        Every WPA creator learns to build a professional media kit like this one from our founder, Shawn B.
      </p>
      <div className="mt-14 bg-white rounded-[32px] border-4 border-ink shadow-pop-pink p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="media-kit-card">
        <KitPhotos />
        <KitDetails />
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <CTA href={BRAND_MAIL} tone="ink" big testid="media-kit-brand-cta">Find Creators for your Brand</CTA>
      </div>
    </div>
  </section>
);

const KitPhotos = () => (
  <div className="lg:col-span-5 grid grid-cols-2 gap-4 self-start">
    <img src="/media-kit/shawn-2.jpg" alt="Shawn B smiling" className="col-span-2 w-full h-[360px] sm:h-[440px] object-cover object-top rounded-t-[999px] rounded-b-[28px] border-4 border-ink" data-testid="media-kit-photo-main" />
    <img src="/media-kit/shawn-3.jpg" alt="Shawn B portrait" className="w-full h-[180px] object-cover rounded-[24px] border-2 border-ink" />
    <div className="bg-lime rounded-[24px] border-2 border-ink flex flex-col items-center justify-center text-center p-3">
      <Music2 size={22} />
      <span className="font-display text-[12px] sm:text-[17px] mt-2" data-testid="media-kit-handle">@shawnbailey218</span>
    </div>
  </div>
);

const KitDetails = () => (
  <div className="lg:col-span-7 flex flex-col gap-6">
    <div>
      <h3 className="font-display text-ink text-[44px] sm:text-[60px] leading-none" data-testid="media-kit-name">Shawn B.</h3>
      <p className="mt-2 text-[13px] font-bold tracking-[0.2em] uppercase text-pink">Social Media Strategist · Los Angeles, CA</p>
    </div>
    <div className="grid grid-cols-3 gap-3">
      {KIT_STATS.map(([v, l, c]) => (
        <div key={l} className={`${c} rounded-[24px] border-2 border-ink p-2 sm:p-4 text-center`} data-testid={`media-kit-stat-${v}`}>
          <div className="font-display text-[30px] sm:text-[44px] leading-none">{v}</div>
          <div className="mt-2 text-[9px] sm:text-[12px] font-semibold uppercase sm:tracking-wide leading-tight">{l}</div>
        </div>
      ))}
    </div>
    <div className="space-y-3 text-ink/75 text-[16px] leading-relaxed">
      <p className="font-display text-ink text-[20px]">About me</p>
      <p>Shawn is a Los Angeles native, dynamic entrepreneur, social media strategist, and community advocate with a unique blend of heart, hustle, and digital savvy.</p>
      <p>In 2025, Shawn partnered with TikTok to launch Wanderpreneur Agency (WPA), a creator-focused social media agency that teaches content creators and business owners how to navigate TikTok, monetize their presence, and build a sustainable digital brand.</p>
      <p>Shawn is known for her live-stream coaching, platform growth strategies, and ability to turn "just posting" into purposeful, revenue-driven content.</p>
    </div>
    <blockquote className="border-l-4 border-pink pl-5 font-display text-ink text-[19px] sm:text-[22px] leading-snug italic" data-testid="media-kit-quote">
      "Social media is a tool for income, identity, and impact — when creators have strategy and commitment, they can build a sustainable business and a meaningful community." <span className="not-italic text-pink">— Shawn B</span>
    </blockquote>
    <div className="flex flex-wrap gap-x-6 gap-y-2 text-[14px] font-medium text-ink/70">
      <a href="mailto:WPAAgency2026@gmail.com" className="hover:text-pink" data-testid="media-kit-email">WPAAgency2026@gmail.com</a>
      <a href="https://www.wanderpreneuragency.com" target="_blank" rel="noopener noreferrer" className="hover:text-pink" data-testid="media-kit-website">wanderpreneuragency.com</a>
    </div>
  </div>
);

const VOICES = [
  [
    "Joining WPA has been one of the best decisions I've made on my TikTok journey. This agency isn't just about growth in numbers. It's about growth in every aspect of your life.\n\nWhen I met Shawn B, I wasn't in the best financial or mental space. But she saw something in me that I couldn't see in myself. She didn't just mentor me. She poured into me, motivated me, and helped me reconnect with my WHY. She's more than a mentor… she's become family. A real one. My sister. Because of WPA, I've grown not only in followers and monetization, but also in confidence, discipline, and mindset.",
    "@daileydoesofMary, USA",
    "lime",
    true,
  ],
  [
    "This community pushes you to be better, to think bigger, and to execute. I'm beyond grateful for Shawn and my whole WPA family. We're not just building platforms—we're building legacies.",
    "",
    "pink",
  ],
  [
    "I've been with the Wanderpreneur Agency since its' inception and I have had one of the best experiences with our team. Anytime I have an issue it is resolved quickly. This agency is awesome — join, you won't be disappointed.",
    "Jaazer Craig",
    "sky",
  ],
];

const Voices = () => (
  <section id="voices" className="relative bg-white">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32">
      <SectionLabel n="07" color="pink">Creator voices</SectionLabel>
      <h2
        className="font-display text-ink leading-[1] max-w-[18ch]"
        style={{ fontSize: "clamp(40px, 6vw, 92px)" }}
      >
        What our creators <span className="text-pink">actually say.</span>
      </h2>
      <p className="mt-6 text-ink/70 text-[17px] leading-relaxed max-w-[60ch]">
        Real words from creators in the network. No affiliates, no sponsored posts.
      </p>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {VOICES.map(([q, author, c, noQuotes], i) => {
          const chip = { lime: "bg-lime", pink: "bg-pink text-white", sky: "bg-sky" }[c];
          return (
            <figure
              key={i}
              className="relative rounded-[28px] border-2 border-ink bg-white p-8 flex flex-col justify-between min-h-[340px]"
              data-testid={`voice-${i}`}
            >
              <div className={`absolute -top-4 left-6 ${chip} border-2 border-ink rounded-full px-3 py-1 text-[11px] font-bold tracking-wider uppercase`}>
                Verified
              </div>
              <blockquote className="text-ink text-[16px] leading-[1.55] font-medium whitespace-pre-line">
                {noQuotes ? q : `"${q}"`}
              </blockquote>
              {author && (
                <figcaption className="mt-6 font-display text-ink text-[18px]">
                  {author}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>
    </div>
  </section>
);

const Closer = () => (
  <section className="relative">
    <div className="relative overflow-hidden">
      <img
        src={CLOSE_IMG}
        alt="Creator smiling during a livestream"
        className="w-full h-[560px] sm:h-[680px] object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-plum via-plum/60 to-transparent" />
      <div className="absolute inset-0 flex items-center">
        <div className="px-6 sm:px-10 max-w-[1400px] w-full mx-auto">
          <Pill tone="lime" className="mb-6"><Star size={14}/> one last thing</Pill>
          <h2
            className="font-display text-white leading-[1] max-w-[22ch]"
            style={{ fontSize: "clamp(38px, 5.5vw, 82px)" }}
            data-testid="closer-quote"
          >
            "Bring your <span className="text-coral">fire;</span> we'll refine
            the spark into a{" "}
            <span className="text-lime">sustainable blaze.</span>"
          </h2>
          <div className="mt-10 flex flex-wrap gap-3">
            <CTA href="https://form.jotform.com/243437768454064" tone="lime" big testid="closer-cta">
              Join Us
            </CTA>
            <CTA href="mailto:WPAAgency2026@gmail.com" tone="pink" big testid="closer-email">
              <Mail size={16} /> Email Us
            </CTA>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="relative bg-ink">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-14 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <div className="font-display text-white text-[32px]">
          Wanderpreneur<span className="text-pink">.</span>
        </div>
        <p className="mt-4 text-cream/70 text-[14px] leading-relaxed max-w-[42ch]">
          Connecting Brands with Creators. Empowering Creators with
          Opportunity. Our mission is to cultivate capable, confident creators
          and build meaningful partnerships that benefit both creators and
          brands. Serving US · Canada · UK.
        </p>
      </div>

      <div className="md:col-span-3">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-cream/50 mb-4">
          Navigate
        </p>
        <ul className="space-y-2">
          {[
            ["Network", "#network"],
            ["Benefits", "#benefits"],
            ["Voices", "#voices"],
          ].map(([l, h]) => (
            <li key={l}>
              <a href={h} className="text-cream/80 text-[15px] hover:text-pink transition-colors">
                {l}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-4">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-cream/50 mb-4">
          Get in
        </p>
        <div className="flex flex-col gap-3">
          <a
            href={JOTFORM}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-apply"
            className="inline-flex items-center justify-between gap-3 bg-lime text-ink px-5 py-3.5 rounded-full text-[13px] font-semibold hover:bg-pink hover:text-white transition-colors"
          >
            Apply <ArrowUpRight size={14} />
          </a>
          <a
            href="mailto:WPAAgency2026@gmail.com"
            data-testid="footer-email"
            className="inline-flex items-center justify-between gap-3 bg-pink text-white px-5 py-3.5 rounded-full text-[13px] font-semibold hover:bg-lime hover:text-ink transition-colors"
          >
            <span className="flex items-center gap-2"><Mail size={14} /> WPAAgency2026@gmail.com</span>
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] font-medium text-cream/50">
        <span>© 2026 Wanderpreneur Agency · All rights reserved</span>
        <a href="/admin" className="hover:text-pink transition-colors" data-testid="footer-team-login">Team login</a>
      </div>
    </div>
  </footer>
);

/* ---------- page ---------- */
export default function Home() {
  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-white" data-testid="home-root">
      <Toaster position="top-center" />
      <Nav />
      <Hero />
      <Stats />
      <Network />
      <Benefits />
      <Marquee />
      <WhoShouldJoin />
      <Pillars />
      <Services />
      <MediaKit />
      <BrandEnquiry />
      <FeeFAQ />
      <Voices />
      <Closer />
      <Footer />
    </div>
  );
}
